import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { stripeService } from '../services/stripeService';
import errorTrackingService from '../services/errorTrackingService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          // Check if pilot mode has expired
          if (stripeService?.isPilotExpired(data)) {
            console.log('⚠️ Pilot mode expired, downgrading user to free plan...');
            const { data: downgradedProfile, error: downgradeError } = await stripeService?.downgradePilotUser(userId);
            
            if (!downgradeError && downgradedProfile) {
              setUserProfile(downgradedProfile);
              return; // Exit early with downgraded profile
            } else {
              console.error('Failed to downgrade pilot user:', downgradeError);
              errorTrackingService?.captureError(new Error('Failed to downgrade pilot user'), {
                errorCategory: 'StateManagement',
                stateType: 'UserProfile',
                operation: 'downgradePilotUser',
                userId,
                error: downgradeError
              });
              // Continue with existing profile if downgrade fails
            }
          }
          
          setUserProfile(data)
          
          // CRITICAL: Always sync subscription status with Stripe on every app load
          // This ensures real-time subscription verification without manual intervention
          // SKIP Stripe sync for pilot mode users (they don't have Stripe subscriptions)
          if (data?.stripe_customer_id && !stripeService?.isPilotMode(data)) {
            // Fire-and-forget: Don't block profile load, but update in background
            profileOperations?.syncSubscriptionStatus(userId, data?.stripe_customer_id)
          }
        } else if (error) {
          errorTrackingService?.captureStateError(
            'UserProfile',
            'load',
            new Error(error.message || 'Failed to load profile'),
            null,
            { userId }
          );
        }
      } catch (error) {
        console.error('Profile load error:', error)
        errorTrackingService?.captureStateError(
          'UserProfile',
          'load',
          error,
          null,
          { userId }
        );
      } finally {
        setProfileLoading(false)
      }
    },

    async syncSubscriptionStatus(userId, stripeCustomerId) {
      try {
        console.log('🔄 Syncing subscription status with Stripe...', { userId, stripeCustomerId })
        
        // Fetch current subscription from Stripe
        const { data: subscriptionData, error } = await stripeService?.getSubscriptionStatus(stripeCustomerId)
        
        if (!error && subscriptionData) {
          console.log('✅ Stripe subscription data received:', subscriptionData)
          
          // CRITICAL: Handle deleted Stripe customers
          if (subscriptionData?.reason === 'customer_deleted') {
            console.log('⚠️ Customer deleted from Stripe, downgrading to free tier')
          }
          
          // Update local profile if Stripe data differs
          const { data: updatedProfile, error: updateError } = await supabase
            ?.from('user_profiles')
            ?.update({
              subscription_tier: subscriptionData?.tier,
              subscription_status: subscriptionData?.status,
              stripe_subscription_id: subscriptionData?.subscription_id,
              subscription_end_date: subscriptionData?.end_date,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', userId)
            ?.select()
            ?.single()
          
          if (!updateError && updatedProfile) {
            console.log('✅ User profile updated with Stripe subscription data:', {
              tier: updatedProfile?.subscription_tier,
              status: updatedProfile?.subscription_status,
              reason: subscriptionData?.reason || 'normal_sync'
            })
            setUserProfile(updatedProfile)
          } else if (updateError) {
            console.error('❌ Failed to update user profile:', updateError)
            errorTrackingService?.captureStateError(
              'UserProfile',
              'syncSubscriptionStatus',
              new Error(updateError.message || 'Failed to update profile'),
              null,
              { userId, subscriptionData }
            );
          }
        } else if (error) {
          console.error('❌ Failed to fetch Stripe subscription status:', error)
          errorTrackingService?.captureApiError(
            '/stripe/subscription-status',
            'GET',
            new Error(error.message || 'Failed to fetch subscription'),
            { stripeCustomerId },
            null
          );
        }
      } catch (error) {
        console.error('❌ Subscription sync error:', error)
        // Don't block profile load on sync failure
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async ({ email, password }) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
